import { useDeferredValue, useEffect, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Eye, Globe, Import, Inbox, Link2, Loader2, Plus, RefreshCw } from 'lucide-react';
import {
  Button,
  Card,
  CardGridSkeleton,
  EmptyState,
  IconButton,
  LoadMore,
  MenuButton,
  PageHeader,
  SearchInput,
  Select,
  TableSkeleton,
  ViewSwitch,
} from '@/components/ui';
import { ErrorState } from '@/components/ErrorState';
import { CreateSiteDrawer } from '@/components/sites/CreateSiteDrawer';
import { NewSiteWizard } from '@/components/sites/NewSiteWizard';
import { SiteOrdersStrip } from '@/components/sites/SiteOrdersStrip';
import { ImportSiteDrawer } from '@/components/sites/ImportSiteDrawer';
import { ObserveSiteDialog } from '@/components/sites/ObserveSiteDialog';
import { SitesGrid } from '@/components/sites/SitesGrid';
import { SitesTable } from '@/components/sites/SitesTable';
import { BulkBar } from '@/components/bulk/BulkBar';
import { BulkRunDrawer } from '@/components/bulk/BulkRunDrawer';
import { TagBar } from '@/components/tags/TagBar';
import { useRoles } from '@/hooks/useAuth';
import { useSites, useSiteSync, useSyncTaggedSites } from '@/hooks/useSites';
import { useListView } from '@/store/preferencesStore';
import { toast } from '@/store/toastStore';
import type { TagSelection } from '@/types/tags';
import { errorText } from '@/utils/errors';
import { can } from '@/utils/permissions';

/**
 * The site list.
 *
 * One screen answers the question an operator actually arrives with — "where is
 * the site I am thinking of" — so the search box covers name, slug and domain,
 * and the tag bar narrows by whatever the team groups by. Everything else about
 * a site lives on its own page.
 */

const TIER_OPTIONS = [
  { value: '', label: 'Все среды' },
  { value: 'production', label: 'Рабочие' },
  { value: 'staging', label: 'Тестовые' },
];

const STATUS_OPTIONS = [
  { value: '', label: 'Любое состояние' },
  { value: 'ready', label: 'Готов' },
  { value: 'provisioning', label: 'Разворачивается' },
  { value: 'failed', label: 'Ошибка' },
  { value: 'suspended', label: 'Приостановлен' },
  { value: 'draft', label: 'Черновик' },
  { value: 'archived', label: 'В архиве' },
];

export function SitesPage(): ReactNode {
  const roles = useRoles();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [tier, setTier] = useState('');
  const [tags, setTags] = useState<TagSelection>({ tagIds: [], untagged: false });
  const [creating, setCreating] = useState(false);
  const [observing, setObserving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [wizard, setWizard] = useState(false);
  // The order the wizard follows, when opened from the strip or a purchase (ADR-0059).
  const [following, setFollowing] = useState<string | null>(null);
  const [view, setView] = useListView('sites');
  // Sites chosen for one action on many (ADR-0046), and the run it started.
  const [chosen, setChosen] = useState<ReadonlySet<string>>(new Set());
  const [bulkRun, setBulkRun] = useState<string | null>(null);
  const toggle = (ids: readonly string[], on: boolean) =>
    setChosen((current) => {
      const next = new Set(current);
      for (const id of ids) {
        if (on) next.add(id);
        else next.delete(id);
      }
      return next;
    });
  // After a sync is queued the list is asked again for a while (useSites).
  const [watchUntil, setWatchUntil] = useState(0);
  const mayCreate = can(roles, 'sites.create');
  // «Создать сайт на …» from a finished domain purchase (ADR-0053): the wizard opens with the domain in it.
  // `?order=` from a purchase that pays for a site order (ADR-0059): the wizard follows that order.
  const [params, setParams] = useSearchParams();
  const handoff = params.get('new') === 'site' ? params.get('domain') : null;
  const orderLink = params.get('order');
  const [presetDomain, setPresetDomain] = useState<string | null>(null);
  useEffect(() => {
    if ((!handoff && !orderLink) || !mayCreate) return;
    if (orderLink) setFollowing(orderLink);
    else setPresetDomain(handoff);
    setWizard(true);
    setParams({}, { replace: true });
  }, [handoff, orderLink, mayCreate, setParams]);
  const siteTag = useSiteSync().data?.tag ?? null;
  const sync = useSyncTaggedSites({
    onSuccess: () => {
      setWatchUntil(Date.now() + 30_000);
      toast.success('Синхронизация с 2d2s запущена', `Домены с тегом «${siteTag}»`);
    },
    onError: (error) => toast.error('Не удалось запросить обновление', errorText(error)),
  });

  // Deferred so typing stays responsive: the table redraws a beat behind the
  // box rather than on every keystroke.
  const query = useDeferredValue(search);

  // Every filter goes to the server. Filtering in the browser would only thin
  // out the page already loaded and quietly hide what lies beyond it.
  const sites = useSites({ search: query, status, tier, tags }, watchUntil);
  const rows = sites.data?.pages.flatMap((page) => page.items) ?? [];
  const total = sites.data?.pages[0]?.total ?? null;
  const filtered = search !== '' || status !== '' || tier !== '' || tags.tagIds.length > 0 || tags.untagged;
  const selection = { chosen, toggle };

  return (
    <div className="page">
      <PageHeader
        title="Сайты"
        actions={
          mayCreate ? (
            <div className="row-tight">
              {siteTag ? (
                <IconButton
                  icon={RefreshCw}
                  label={`Синхронизировать с 2d2s: домены с тегом «${siteTag}»`}
                  busy={sync.isPending}
                  onClick={() => sync.mutate()}
                />
              ) : null}
              <MenuButton
                label="Подключить"
                icon={Link2}
                items={[
                  { label: 'Импорт WordPress', icon: Import, onSelect: () => setImporting(true) },
                  { label: 'Мониторинг внешнего сайта', icon: Eye, onSelect: () => setObserving(true) },
                ]}
              />
              <Button
                variant="primary"
                icon={Plus}
                onClick={() => {
                  setFollowing(null);
                  setWizard(true);
                }}
              >
                Новый сайт
              </Button>
            </div>
          ) : null
        }
      />

      {mayCreate ? (
        <SiteOrdersStrip
          onOpen={(id) => {
            setFollowing(id);
            setWizard(true);
          }}
        />
      ) : null}

      <div className="stack stack-tight">
        <div className="toolbar">
          <div className="toolbar-search">
            <SearchInput label="Поиск по названию или домену" placeholder="Название, slug или домен…" value={search} onChange={setSearch} />
          </div>

          <Select label="Среда" size="sm" value={tier} onChange={setTier} options={TIER_OPTIONS} />
          <Select label="Состояние" size="sm" value={status} onChange={setStatus} options={STATUS_OPTIONS} />

          <span className="row-tight muted text-sm">
            {sites.isFetching ? <Loader2 size={13} className="spin" aria-hidden="true" /> : null}
            {sites.data ? `${rows.length} из ${total ?? rows.length}` : null}
          </span>

          <span className="toolbar-end">
            <ViewSwitch value={view} onChange={setView} />
          </span>
        </div>

        <TagBar selection={tags} onChange={setTags} />
      </div>

      {view === 'cards' && sites.isPending ? (
        <CardGridSkeleton />
      ) : view === 'cards' && rows.length > 0 ? (
        <SitesGrid sites={rows} selection={selection} />
      ) : (
        <Card flush>
          {sites.isPending ? (
            <TableSkeleton rows={6} columns={5} />
          ) : sites.isError ? (
            <ErrorState error={sites.error} />
          ) : rows.length === 0 ? (
            <EmptyState
              icon={filtered ? Inbox : Globe}
              message={
                filtered
                  ? 'Нет сайтов по заданным условиям'
                  : siteTag
                    ? `Сайтов пока нет. Домены с тегом «${siteTag}» в 2d2s добавляются автоматически.`
                    : 'Сайтов пока нет'
              }
            />
          ) : (
            <SitesTable sites={rows} selection={selection} />
          )}
        </Card>
      )}

      <LoadMore
        shown={rows.length}
        total={total}
        hasMore={sites.hasNextPage}
        busy={sites.isFetchingNextPage}
        onMore={() => void sites.fetchNextPage()}
      />

      {chosen.size > 0 ? <BulkBar siteIds={[...chosen]} onClear={() => setChosen(new Set())} onLaunched={(id) => setBulkRun(id)} /> : null}
      <BulkRunDrawer id={bulkRun} onClose={() => setBulkRun(null)} />

      <NewSiteWizard
        open={wizard}
        domain={presetDomain}
        orderId={following}
        onClose={() => {
          setWizard(false);
          setFollowing(null);
          setPresetDomain(null);
        }}
        onOtherKind={() => {
          setWizard(false);
          setCreating(true);
        }}
        onImport={() => {
          setWizard(false);
          setImporting(true);
        }}
        onObserve={() => {
          setWizard(false);
          setObserving(true);
        }}
      />
      <CreateSiteDrawer
        open={creating}
        domain={presetDomain}
        onClose={() => {
          setCreating(false);
          setPresetDomain(null);
        }}
      />
      <ObserveSiteDialog open={observing} onClose={() => setObserving(false)} />
      {importing ? <ImportSiteDrawer open={importing} onClose={() => setImporting(false)} /> : null}
    </div>
  );
}
